import React from 'react';
import { Typography } from 'antd';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import Ninja from '@ninja';
import getFormattedNumber from 'store/redux/utils/getFormattedNumber';
import { DateFormatter } from 'components/charts/formatters';
import { trafficRepotByPeriodMetrics } from './TrafficRepotByPeriodMetrics';
import getShortFormattedNumber from 'store/redux/utils/getShortFormattedNumber';
import ActiveDot from 'components/charts/components/ActiveDot';

const TrafficRepotByPeriodChart = ({ data, activeMetrics }) => {
  const hasRightAxis = activeMetrics.includes('avg_session_duration');
  const showOnlySessionDuration = activeMetrics.includes('avg_session_duration');

  return (
    <ResponsiveContainer width='100%' height={300}>
      <LineChart data={data} margin={{ right: 20, left: -30, top: 10, bottom: 0 }}>
        <CartesianGrid strokeDasharray='2 2' vertical={false} stroke={Ninja.colors.xlight} />
        <ReferenceLine
          y={0}
          yAxisId={hasRightAxis ? 'right' : 'left'}
          stroke={Ninja.colors.xlight}
        />
        <XAxis
          axisLine={false}
          dataKey='date'
          dy={10}
          tickCount={30}
          interval={data.length > 25 ? Math.ceil(data.length / 29) : 0}
          tickFormatter={(value) =>
            new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
          }
          tick={{ fill: Ninja.colors.darkText, fontSize: 12, fontWeight: 450 }}
        />

        <YAxis
          yAxisId='left'
          orientation='left'
          tickFormatter={(value) => getShortFormattedNumber(value)}
          axisLine={false}
          tick={{ fill: Ninja.colors.darkText, fontSize: 12, fontWeight: 450 }}
        />

        {hasRightAxis && (
          <YAxis
            yAxisId='right'
            width={40}
            orientation='right'
            tickFormatter={(value) => {
              const item = data.find((d) => d.avg_session_duration === value);
              return item?.avg_session_duration_str ?? `${Math.floor(value / 60)}m ${value % 60}s`;
            }}
            axisLine={false}
            tick={{ fill: Ninja.colors.darkText, fontSize: 12, fontWeight: 500 }}
          />
        )}

        <Tooltip
          cursor={{
            stroke: Ninja.colors.xlight,
            strokeWidth: 1,
            strokeDasharray: '3 3',
          }}
          content={({ active, payload, label }) => {
            if (!active || !payload?.length) return null;
            return (
              <div
                style={{ background: 'rgba(255,255,255,0.95)' }}
                className='rounded-md px-2 py-2 shadow-lg '
              >
                <Typography strong className='font-medium text-sm mb-2 flex justify-center w-full'>
                  <DateFormatter format='MMM DD, YYYY' date={label} />
                </Typography>
                {payload.map((entry, index) => {
                  const metricConfig = trafficRepotByPeriodMetrics[entry.dataKey] || {};
                  const symbol = metricConfig.symbol || '';
                  let displayValue = entry.value ?? entry.payload?.value;

                  if (entry.dataKey === 'avg_session_duration') {
                    const item = data.find((d) => d.date === label);
                    displayValue = item?.avg_session_duration_str ?? displayValue;
                  } else {
                    displayValue = getFormattedNumber(displayValue) + symbol;
                  }

                  return (
                    <div className='flex justify-between items-center gap-4' key={index}>
                      <div className='flex items-center gap-2'>
                        <div
                          className='rounded-full h-2.5 w-2.5'
                          style={{ background: entry.color }}
                        />
                        <Typography className='!font-normal text-sm'>
                          {metricConfig.title}
                        </Typography>
                      </div>
                      <Typography className='font-medium text-base'>{displayValue}</Typography>
                    </div>
                  );
                })}
              </div>
            );
          }}
        />

        {activeMetrics
          .filter((metric) => metric !== 'avg_session_duration')
          .map((metric) => (
            <Line
              key={metric}
              dot={false}
              type='line'
              dataKey={metric}
              yAxisId='left'
              stroke={trafficRepotByPeriodMetrics[metric]?.color || '#858585ff'}
              strokeWidth={2}
              activeDot={<ActiveDot />}
            />
          ))}

        {showOnlySessionDuration && (
          <Line
            key='avg_session_duration'
            dot={false}
            type='line'
            dataKey='avg_session_duration'
            yAxisId='right'
            stroke={trafficRepotByPeriodMetrics['avg_session_duration']?.color || '#858585ff'}
            strokeWidth={2}
            activeDot={<ActiveDot />}
          />
        )}
      </LineChart>
    </ResponsiveContainer>
  );
};

export default TrafficRepotByPeriodChart;
